import { Component, signal } from '@angular/core';
import { TourService } from 'ngx-ui-tour-md-menu';
import { AuthSelectors } from '@store/auth';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import { environment } from '@env/environment';
import { OneSignal } from 'onesignal-ngx';
import { IUser } from '@models/user';
import { SettingsSelectors } from '@store/settings';
import { IOrganisation } from '@models/organisation';
import { LocalStorageService } from '@core/services/services/local-storage.service';
import { TimezoneManagerService } from '@core/services/services/timezone-manager.service';
import { ChatwootService } from '@core/services/services/chatwoot.service';
import { SentryContextService } from '@core/services/observability/sentry-context.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
    userImpersonatingMode = signal<boolean>(false);

    constructor(
        public readonly tourService: TourService,
        private authSelectors: AuthSelectors,
        private settingsSelectors: SettingsSelectors,
        private oneSignal: OneSignal,
        localStorage: LocalStorageService,
        private timezoneManagerService: TimezoneManagerService,
        private chatwootService: ChatwootService,
        private sentryContext: SentryContextService
    ) {
        if (environment.sentryUrl) {
            this.sentryContext.start();
        }

        localStorage.migrate();

        this.settingsSelectors.organisation$
            .pipe(
                filter(organization => !!organization),
                withLatestFrom(authSelectors.user$),
                take(1)
            )
            .subscribe(([organization, user]) => {
                this.configureWebPushNotifications(user!, organization);
                this.initializeTimezoneDetection(user!);
                this.initializeChatwoot(organization);

                if (organization.user_impersonating_mode) {
                    this.userImpersonatingMode.set(true);
                }
            });
    }

    private initializeTimezoneDetection(user: IUser): void {
        this.timezoneManagerService.initializeTimezoneDetection(user).subscribe({
            next: () => {
                console.info('Timezone detection initialized');
            },
            error: error => {
                console.error('Failed to initialize timezone detection:', error);
            }
        });
    }

    private initializeChatwoot(organization: IOrganisation): void {
        if (!environment.chatwoot?.enabled) {
            return;
        }

        const chatwootConnection = organization.connections?.find(
            connection => connection.connectable_type === 'chatwoot' && connection.status === 'active'
        );

        if (chatwootConnection) {
            this.chatwootService.initialize();
        }
    }

    private configureWebPushNotifications(user: IUser, organization: IOrganisation): void {
        if (environment.type === 'qa') {
            return;
        }

        const onesignalConnection = organization.connections!.find(
            connection => connection.connectable_type === 'onesignal' && connection.status === 'active'
        );

        if (onesignalConnection) {
            this.oneSignal.setExternalUserId(user.id.toString());

            this.oneSignal.init({
                appId: onesignalConnection.connectable.app_id,
                safari_web_id: onesignalConnection.connectable.safari_web_id,
                allowLocalhostAsSecureOrigin: environment.type === 'development'
            });
        }
    }
}
